import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import MovieCard from '../components/MovieCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [trendingMovies, setTrendingMovies] = useState([]);
  const [popularMovies, setPopularMovies] = useState([]);
  const [heroMovie, setHeroMovie] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [trendingRes, popularRes, randomRes] = await Promise.all([
        axios.get(`${API}/movies/trending?time_window=week&page=1`),
        axios.get(`${API}/movies/popular?page=1`),
        axios.get(`${API}/movies/random`),
      ]);

      setTrendingMovies(trendingRes.data.results?.slice(0, 10) || []);
      setPopularMovies(popularRes.data.results?.slice(0, 10) || []);
      setHeroMovie(randomRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load movies');
    } finally {
      setLoading(false);
    }
  };

  const handleRandomMovie = async () => {
    try {
      const res = await axios.get(`${API}/movies/random`);
      if (res.data && res.data.id) {
        window.location.href = `/movie/${res.data.id}`;
      }
    } catch (error) {
      toast.error('Failed to find a random movie');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#7FFF7F] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading trailers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      <section className="relative h-screen w-full overflow-hidden">
        {heroMovie?.backdrop_path && (
          <>
            <img
              src={`https://image.tmdb.org/t/p/original${heroMovie.backdrop_path}`}
              alt={heroMovie.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-cinema-black via-cinema-black/70 to-cinema-black/30"></div>
          </>
        )}

        <div className="relative h-full flex flex-col justify-end pb-20 md:pb-24 px-6 md:px-12 max-w-7xl mx-auto pt-40">
          <div className="animate-fade-in space-y-4 max-w-4xl">
            <h1
              className="font-anton text-3xl sm:text-4xl md:text-5xl text-white uppercase tracking-tight leading-tight break-words drop-shadow-[0_4px_20px_rgba(0,0,0,0.9)]"
              data-testid="hero-title"
            >
              {heroMovie?.title || 'The Movie Matrix'}
              {heroMovie?.release_date && (
                <span className="text-3xl sm:text-4xl md:text-5xl">
                  {' '}({heroMovie.release_date.split('-')[0]})
                </span>
              )}
            </h1>
            <p className="text-base md:text-lg text-gray-100 leading-relaxed max-w-3xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)] line-clamp-3">
              {heroMovie?.overview ||
                'Explore movie trailers from the beginning of cinema to the present day. Discover, watch, and relive the magic of film.'}
            </p>
            <div className="flex items-center gap-4 pt-3">
              {heroMovie?.id && (
                <Button
                  size="lg"
                  className="bg-[#7FFF7F] hover:bg-[#6FEF6F] text-black font-semibold px-8 py-6 rounded-lg shadow-[0_0_20px_rgba(127,255,127,0.5)] hover:shadow-[0_0_30px_rgba(127,255,127,0.7)] transition-all duration-300"
                  onClick={() => (window.location.href = `/movie/${heroMovie.id}`)}
                  data-testid="hero-watch-button"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Watch Trailer
                </Button>
              )}
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/20 text-white hover:bg-white/10 hover:border-[#7FFF7F]/40 font-semibold px-8 py-6 rounded-lg backdrop-blur-sm transition-all duration-300"
                onClick={handleRandomMovie}
                data-testid="random-movie-button"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Random Trailer
              </Button>
            </div>
          </div>
          <div className="w-full flex justify-center pt-8">
            <p className="text-[#7FFF7F] uppercase tracking-[0.2em] text-sm md:text-base font-semibold text-center drop-shadow-[0_2px_8px_rgba(127,255,127,0.3)]">
              The Past, Present, and Future of Film
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto" data-testid="trending-section">
        <div className="mb-8">
          <h2 className="font-anton text-4xl md:text-5xl text-white uppercase mb-2">
            Trending This Week
          </h2>
          <p className="text-gray-400">The hottest trailers everyone's watching</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {trendingMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      <section className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto" data-testid="popular-section">
        <div className="mb-8">
          <h2 className="font-anton text-4xl md:text-5xl text-white uppercase mb-2">
            All-Time Favorites
          </h2>
          <p className="text-gray-400">Classic trailers that never get old</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {popularMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;